import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import * as adminApi from '../../api/admin.api';
import type { AdminNode } from '../../types/admin';
import styles from './AdminNodes.module.css';

export function AdminNodes() {
  const { user } = useAuth();
  const [nodes, setNodes] = useState<AdminNode[]>([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNodes = async () => {
    try {
      const data = await adminApi.getNodes();
      setNodes(data);
    } catch {
      setNodes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNodes();
    intervalRef.current = setInterval(fetchNodes, 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  if (user?.role !== 'admin') {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied}>Доступ запрещён</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Ноды</h1>
        <span className={styles.refreshInfo}>Обновление каждые 30 сек.</span>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spinner size="lg" />
        </div>
      ) : nodes.length === 0 ? (
        <div className={styles.emptyState}>Ноды не найдены</div>
      ) : (
        <div className={styles.nodesGrid}>
          {nodes.map((node) => (
            <Card key={node.id} className={styles.nodeCard}>
              <div
                className={`${styles.nodeIcon} ${
                  node.status === 'online' || node.status === 'connected' ? styles.nodeIconOnline : styles.nodeIconOffline
                }`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="8" rx="2" />
                  <rect x="2" y="14" width="20" height="8" rx="2" />
                  <line x1="6" y1="6" x2="6.01" y2="6" />
                  <line x1="6" y1="18" x2="6.01" y2="18" />
                </svg>
              </div>

              <div className={styles.nodeInfo}>
                <div className={styles.nodeName}>{node.name}</div>
                <div className={styles.nodeHost}>{node.host}</div>
                <div className={styles.nodeMeta}>
                  <span>{node.usersCount} пользователей</span>
                </div>
              </div>

              <div className={styles.nodeStatus}>
                <Badge variant={node.status === 'online' || node.status === 'connected' ? 'success' : 'error'}>
                  {node.status === 'online' || node.status === 'connected' ? 'Online' : 'Offline'}
                </Badge>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
